class CartDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get requiresBodyAppended() {
    return false;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open) {
      // CRITICAL FIX: Triple requestAnimationFrame chain to prevent freeze when opened
      // from quick-view modal (e.g., "Add to Cart" from product quick view)
      //
      // Frame 1: Browser repaints drawer visibility and position
      // Frame 2: Quick-view modal's handleAfterHide() cleanup completes (clears DOM)
      // Frame 3: THEN call trapFocus() to scan DOM for focusable elements
      //
      // Without this, trapFocus() runs synchronously while quick-view is still
      // clearing its DOM, causing expensive querySelectorAll scans during mutations.
      // This creates a blocking JS-to-DOM-to-JS cycle that freezes the UI.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            FoxTheme.a11y.trapFocus(this, this.focusElement);
          });
        });
      });
    }
  }
}
customElements.define('cart-drawer', CartDrawer);
class CartQuantity extends QuantitySelector {
  constructor() {
    super();
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.quantityUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate,
      this.checkQuantityRules.bind(this)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  checkQuantityRules() {
    // CRITICAL FIX: Null safety check for this.input
    if (!this.input) {
      if (window.CartDebug) console.warn('CartQuantity.input not found in DOM');
      return;
    }

    const value = parseInt(this.input.value, 10);

    // Validate that value is a valid number (not NaN)
    if (isNaN(value)) {
      if (window.CartDebug) {
        console.warn('CartQuantity value is not a valid number:', this.input.value);
      }
      return;
    }

    if (this.input.min) {
      const buttonMinus = this.querySelector('[name="minus"]');
      const minValue = parseInt(this.input.min, 10);
      if (buttonMinus && !isNaN(minValue)) {
        buttonMinus.disabled = value <= minValue;
      }
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('[name="plus"]');
      const maxValue = parseInt(this.input.max, 10);
      if (buttonPlus && !isNaN(maxValue)) {
        buttonPlus.disabled = value >= maxValue;
      }
    }
  }
}

customElements.define('cart-quantity', CartQuantity);

class CartItems extends HTMLElement {
  constructor() {
    super();

    // Conditional logging setup
    const isDev =
      window.location.hostname === 'localhost' ||
      new URLSearchParams(window.location.search).has('preview_theme_id');
    // eslint-disable-next-line no-console
    this.log = isDev ? console.log.bind(console) : () => {};
    this.warn = isDev ? console.warn.bind(console) : () => {};
    this.error = isDev ? console.error.bind(console) : () => {};

    this.boundOnChange = FoxTheme.utils.debounce(this.onChange.bind(this), 300);
    this.addEventListener('change', this.boundOnChange);

    // Delegation fallback for [is="cart-remove-item"] clicks.
    //
    // Customized built-in elements (class CartRemoveItem extends HTMLAnchorElement)
    // are NOT upgraded when cart HTML is re-injected via DOMParser + replaceChildren():
    // nodes come from a separate document where CartRemoveItem was never registered,
    // so the browser treats them as plain <a> elements and never calls the constructor.
    // Safari never supports customized built-ins at all.
    //
    // Without this delegation, those bare <a> elements follow their href
    // (item.url_to_remove = /cart/change?line=N&quantity=0&return_to=back),
    // triggering a full page navigation → beforeunload loading screen → page reload.
    //
    // CartRemoveItem calls stopPropagation() when it IS properly upgraded (initial
    // page load, before the first DOM update), so only one handler fires per click.
    this.boundHandleRemoveClick = (event) => {
      const removeButton = event.target.closest('[is="cart-remove-item"]');
      if (!removeButton) return;
      event.preventDefault();
      this.updateQuantity(
        removeButton.dataset.index,
        0,
        undefined,
        undefined,
        removeButton.dataset.key
      );
    };
    this.addEventListener('click', this.boundHandleRemoveClick);

    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    // CRITICAL FIX: querySelectorAll never returns null, always returns NodeList
    // Check length instead of truthiness
    if (this.cartItemProducts && this.cartItemProducts.length > 0) {
      this.cartItemProducts.forEach((cartItemProduct) => {
        const template = cartItemProduct.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = cartItemProduct.querySelector('.cart-item__product--info');
        if (parent && template && templateContent) {
          parent.appendChild(templateContent);
        }
      });
    }
  }

  cartUpdateUnsubscriber = undefined;
  _updateCounter = 0;
  _pendingUpdates = new Set();

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.boundOnChange) {
      this.removeEventListener('change', this.boundOnChange);
      this.boundOnChange = null;
    }

    if (this.boundHandleRemoveClick) {
      this.removeEventListener('click', this.boundHandleRemoveClick);
      this.boundHandleRemoveClick = null;
    }
  }

  onChange(event) {
    // EDGE CASE FIX: Validate quantity is >= 1 before updating
    const quantity = parseInt(event.target.value, 10);

    if (isNaN(quantity) || quantity < 0) {
      this.warn('Invalid quantity entered:', event.target.value);
      // Reset to previous value
      event.target.value = event.target.dataset.value || 1;
      return;
    }

    if (quantity === 0) {
      this.showRemoveConfirm(event.target);
      return;
    }

    this.updateQuantity(
      event.target.dataset.index,
      quantity,
      document.activeElement.getAttribute('name'),
      event.target,
      event.target.dataset.key
    );
  }

  showRemoveConfirm(input) {
    const existing = document.getElementById('cart-remove-confirm');
    if (existing) existing.remove();

    const popover = document.createElement('div');
    popover.id = 'cart-remove-confirm';
    popover.className = 'cart-remove-confirm';
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-modal', 'true');
    popover.setAttribute('aria-label', 'Remove item?');

    const text = document.createElement('p');
    text.className = 'cart-remove-confirm__text';
    text.textContent = 'Remove this item?';

    const actions = document.createElement('div');
    actions.className = 'cart-remove-confirm__actions';

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className =
      'cart-remove-confirm__btn cart-remove-confirm__btn--remove btn btn--primary btn--small';
    removeBtn.textContent = 'Remove';

    const keepBtn = document.createElement('button');
    keepBtn.type = 'button';
    keepBtn.className =
      'cart-remove-confirm__btn cart-remove-confirm__btn--keep btn btn--secondary btn--small';
    keepBtn.textContent = 'Keep';

    actions.appendChild(removeBtn);
    actions.appendChild(keepBtn);
    popover.appendChild(text);
    popover.appendChild(actions);
    input.insertAdjacentElement('afterend', popover);

    const close = () => {
      popover.remove();
      document.removeEventListener('click', onOutsideClick);
      document.removeEventListener('keydown', onKeyDown);
    };

    const onOutsideClick = (e) => {
      if (!popover.contains(e.target) && e.target !== input) close();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        input.value = input.dataset.value || 1;
        close();
        input.focus();
      }
    };

    removeBtn.addEventListener('click', () => {
      close();
      this.updateQuantity(input.dataset.index, 0, null, input, input.dataset.key);
    });

    keepBtn.addEventListener('click', () => {
      input.value = input.dataset.value || 1;
      close();
      input.focus();
    });

    popover.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      const focusable = [removeBtn, keepBtn];
      const idx = focusable.indexOf(document.activeElement);
      const next = e.shiftKey
        ? (idx - 1 + focusable.length) % focusable.length
        : (idx + 1) % focusable.length;
      focusable[next].focus();
    });

    setTimeout(() => {
      document.addEventListener('click', onOutsideClick);
      document.addEventListener('keydown', onKeyDown);
      removeBtn.focus();
    }, 0);
  }

  onCartUpdate(event) {
    // CRITICAL FIX: Validate event data before processing
    if (!event || !event.cart) {
      this.error('Invalid cart update event received');
      return;
    }

    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target || null, event.line);
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);

    // Increment counter to track this update and prevent race conditions
    const updateId = ++this._updateCounter;

    // Create callback that checks component is still attached
    const updateCallback = () => {
      // Skip if component was removed from DOM
      if (!this.isConnected) {
        this.log(`Component disconnected, skipping DOM update for section ${sectionId}`);
        return;
      }

      // Skip if a newer update has been queued
      if (updateId !== this._updateCounter) {
        this.log(`Skipping stale update ${updateId}, current is ${this._updateCounter}`);
        return;
      }

      this.updateCartDOM(event, sectionId);
    };

    // Performance fix: Defer DOMParser only when section HTML is large enough to
    // cause visible jank (>40KB). Small carts (1-5 items, ~5-20KB) render
    // synchronously for instant total/line-item updates after the network responds.
    const sectionHtmlSize = event?.cart?.sections?.[sectionId]?.length || 0;
    if (sectionHtmlSize > 40000) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(updateCallback, { timeout: 150 });
      } else {
        // Use setTimeout instead of requestAnimationFrame (fixes Safari iOS janky UI)
        setTimeout(updateCallback, 0);
      }
    } else {
      updateCallback();
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  updateCartDOM(event, sectionId) {
    // If section HTML is missing from the response (e.g. sections: [] was sent),
    // fall back to a fresh GET of the cart with this section to get renderable HTML.
    // NOTE: Must use /cart.js (AJAX endpoint) — /cart does not return section HTML.
    if (!event?.cart?.sections?.[sectionId]) {
      this.warn(`Section ${sectionId} not found in cart response — fetching separately`);
      (window._nativeFetch || fetch)(`${FoxTheme.routes.cart_url}.js?sections=${sectionId}`, {
        headers: { Accept: 'application/json' },
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((freshCart) => {
          if (freshCart?.sections?.[sectionId]) {
            this.updateCartDOM({ ...event, cart: freshCart }, sectionId);
          } else {
            // Section still absent — at least hide the loader
            if (event?.line !== undefined) this._hideLoaderForLine(event.line);
          }
        })
        .catch((e) => {
          this.error('[CartItems] updateCartDOM fetch failed:', e);
          if (event?.line !== undefined) this._hideLoaderForLine(event.line);
        });
      return;
    }

    const sectionToRender = new DOMParser().parseFromString(
      event.cart.sections[sectionId],
      'text/html'
    );

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);

    if (cartDrawer) {
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(
        `#CartDrawerFooter-${sectionId}`
      );
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      // Add null checks for all current DOM elements
      if (cartDrawerBodyUpdate && cartDrawerBody) {
        cartDrawerBody.replaceChildren(...Array.from(cartDrawerBodyUpdate.children));
      }
      if (cartDrawerFooterUpdate && cartDrawerFooter) {
        cartDrawerFooter.replaceChildren(...Array.from(cartDrawerFooterUpdate.children));
      }

      if (cartDrawerEmptyUpdate && cartDrawerEmpty) {
        cartDrawerEmpty.replaceChildren(...Array.from(cartDrawerEmptyUpdate.children));
      }

      // Safe edge case handling for empty cart
      const itemCount = parseInt(event.cart?.item_count, 10) || 0;

      // Add null checks before class mutations
      if (cartDrawerBody && cartDrawerFooter && cartDrawerEmpty) {
        if (itemCount > 0) {
          cartDrawerBody.classList.remove('hidden');
          cartDrawerFooter.classList.remove('hidden');
          cartDrawerEmpty.classList.add('hidden');
        } else {
          cartDrawerBody.classList.add('hidden');
          cartDrawerFooter.classList.add('hidden');
          cartDrawerEmpty.classList.remove('hidden');
        }
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.replaceChildren(...Array.from(updatedElement.children));
      } else {
        mainCart.closest('.cart')?.classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) ||
      document.getElementById(`CartDrawer-Item-${event.line}`);

    // Defer focus trap operations to prevent blocking the main thread
    // during cart update DOM mutations.
    // Use isConnected check: mainCart may have been .remove()d above for empty-cart case.
    requestAnimationFrame(() => {
      const liveMainCart = mainCart?.isConnected ? mainCart : null;
      const focusContainer = liveMainCart || cartDrawer;
      if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
        if (focusContainer) {
          FoxTheme.a11y.trapFocus(focusContainer, lineItem.querySelector(`[name="${event.name}"]`));
        }
      } else if (event.cart.item_count === 0) {
        if (cartDrawer) {
          FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'));
        } else {
          const emptyEl = document.querySelector('.cart__empty');
          if (emptyEl) FoxTheme.a11y.trapFocus(emptyEl, emptyEl.querySelector('a'));
        }
      } else {
        if (cartDrawer) {
          FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'));
        } else if (liveMainCart) {
          FoxTheme.a11y.trapFocus(liveMainCart, liveMainCart.querySelector('.cart-item__title'));
        }
      }
    });
  }

  updateQuantity(line, quantity, name, target, key) {
    if (this._pendingUpdates.has(line)) {
      this.log(`Update already in flight for line ${line}, ignoring duplicate`);
      return;
    }

    this._pendingUpdates.add(line);

    // For remove actions use optimistic UI: immediately remove the item from DOM before
    // any network call. Third-party apps (swym, recurpay) wrap both window.fetch and
    // XMLHttpRequest in ways that can prevent callbacks from firing.
    // Optimistic removal gives instant visual feedback independent of any callback.
    // _fireRemoveAndSync sends the POST via native XHR (captured before swym loaded)
    // and uses a 600ms watchdog fetch as insurance if XHR callbacks are swallowed.
    if (quantity === 0) {
      this._optimisticallyRemoveItem(line);
      this._fireRemoveAndSync(line, name, target, key);
      return;
    }

    // Quantity update (non-zero) — use _postCartChange (uses native XHR captured
    // before swym wrapped it) and key-based POST body (no stale line index → no 422).
    this.showLoader(line);

    const sectionsToBundle = this._bundleSections();

    // Prefer key-based update (no stale line index problem) with line as fallback.
    const changeBody = key
      ? { id: key, quantity, sections: sectionsToBundle }
      : { line: parseInt(line, 10), quantity, sections: sectionsToBundle };

    const self = this;

    // Recovery fetch for stale-index 422: GET /cart.js (with sections so re-render works).
    // Uses nativeFetch (captured before swym/recurpay loaded) — confirmed to complete through swym.
    const recoverFromServer = (onFail) => {
      const nativeFetch = window._nativeFetch || window.fetch.bind(window);
      const url =
        sectionsToBundle.length > 0
          ? `${FoxTheme.routes.cart_url}.js?sections=${sectionsToBundle.join(',')}`
          : `${FoxTheme.routes.cart_url}.js`;
      nativeFetch(url)
        .then((r) => r.json())
        .then((freshCart) => {
          self._pendingUpdates.delete(line);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: freshCart,
            target,
            line,
            name,
          });
        })
        .catch((e) => {
          self.error('[CartItems] recoverFromServer failed:', e);
          self._pendingUpdates.delete(line);
          onFail();
        });
    };

    this._postCartChange(changeBody, {
      onLoad: (xhr) => {
        const httpStatus = xhr.status;
        let parsedState;
        try {
          parsedState = JSON.parse(xhr.responseText);
        } catch (_e) {
          self._hideLoaderForLine(line);
          self.displayQuantityError('Unable to update cart. Please try again.');
          self._pendingUpdates.delete(line);
          return;
        }
        if (httpStatus >= 400) {
          const errStr =
            (typeof parsedState.errors === 'string' ? parsedState.errors : '') +
            (parsedState.description || '');
          const isStaleIndex = errStr.toLowerCase().includes('line parameter is invalid');
          if (isStaleIndex) {
            recoverFromServer(() => {
              self._hideLoaderForLine(line);
              self.displayQuantityError('Unable to update cart. Please refresh the page.');
            });
          } else {
            self._hideLoaderForLine(line);
            self.displayQuantityError('Unable to update cart. Please try again.');
            self._pendingUpdates.delete(line);
          }
          return;
        }
        self._hideLoaderForLine(line);
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: parsedState,
          target,
          line,
          name,
        });
        self._pendingUpdates.delete(line);
      },
      onNetworkError: () => {
        self.error('Cart update network error');
        self._hideLoaderForLine(line);
        self.displayQuantityError('Unable to update cart. Please try again.');
        self._pendingUpdates.delete(line);
      },
    });
  }

  // Collect the section IDs to bundle into /cart/change requests.
  // Fires the cart:grouped-sections event so other components can add their section IDs,
  // then falls back to this element's own section ID if none were provided.
  _bundleSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    if (sections.length === 0) {
      try {
        const id = FoxTheme.utils.getSectionId(this);
        if (id) sections.push(id);
      } catch (e) {
        this.warn('getSectionId failed:', e);
      }
    }
    return sections;
  }

  // Shared XHR boilerplate for POST /cart/change requests.
  // Uses window._nativeXHR (captured before swym/recurpay loaded) to bypass wrapper interference.
  // Returns the XHR instance.
  _postCartChange(body, { onLoad, onNetworkError }) {
    const xhr = new (window._nativeXHR || XMLHttpRequest)();
    xhr.open('POST', FoxTheme.routes.cart_change_url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.addEventListener('load', () => onLoad(xhr));
    xhr.addEventListener('error', onNetworkError);
    xhr.send(JSON.stringify(body));
    return xhr;
  }

  // Immediately remove item from DOM and update count badges/empty state.
  // Called synchronously before any network request so the user sees the change instantly.
  _optimisticallyRemoveItem(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);

    const drawerItem = document.getElementById(`CartDrawer-Item-${line}`);
    const mainCartItem = document.getElementById(`CartItem-${line}`);
    const removedQty =
      parseInt(drawerItem?.dataset?.quantity ?? mainCartItem?.dataset?.quantity, 10) || 1;

    if (drawerItem) drawerItem.remove();
    if (mainCartItem) mainCartItem.remove();

    // Update all cart-count badges
    document.querySelectorAll('cart-count').forEach((el) => {
      const next = Math.max(0, (parseInt(el.textContent, 10) || 0) - removedQty);
      el.textContent = String(next);
      el.hidden = next === 0;
    });

    // Show empty state if no items remain
    const remaining = this.querySelectorAll('li.cart-item, tr.cart-item').length;
    if (remaining === 0) {
      document.querySelector(`#CartDrawerBody-${sectionId}`)?.classList.add('hidden');
      document.querySelector(`#CartDrawerFooter-${sectionId}`)?.classList.add('hidden');
      document.querySelector(`#CartDrawerEmpty-${sectionId}`)?.classList.remove('hidden');
    }
  }

  // Send the /cart/change POST and sync UI from the response.
  // Uses a 600ms watchdog instead of Promise.race: if the native XHR callback
  // never fires (swym ate it), the watchdog fetches accurate cart state as fallback.
  _fireRemoveAndSync(line, name, target, key) {
    const self = this;
    const nativeFetch = window._nativeFetch || window.fetch.bind(window);

    const sectionsToBundle = this._bundleSections();

    // Prefer key-based removal (immune to stale line indexes) with line as fallback.
    const changeBody = key
      ? { id: key, quantity: 0, sections: sectionsToBundle }
      : { line: parseInt(line, 10), quantity: 0, sections: sectionsToBundle };

    // Shared sync: update badges, total, trigger section re-render.
    // synced flag prevents double-publish when both the watchdog and XHR fire
    // (watchdog fires at 600ms, XHR arrives slightly late — without this guard
    // syncFrom would run twice, causing a duplicate section re-render).
    let synced = false;
    const syncFrom = (freshCart) => {
      if (synced) return;
      synced = true;
      self._pendingUpdates.delete(line);
      if (!freshCart) return;
      document.querySelectorAll('cart-count').forEach((el) => {
        el.textContent = String(freshCart.item_count || 0);
        el.hidden = freshCart.item_count === 0;
      });
      // Direct total update so it reflects the removal before section re-render.
      // try/catch: formatMoney failure must not block the pubsub publish below.
      try {
        if (
          typeof freshCart.total_price === 'number' &&
          FoxTheme?.Currency?.formatMoney &&
          FoxTheme?.settings?.moneyFormat
        ) {
          const formattedTotal = FoxTheme.Currency.formatMoney(
            freshCart.total_price,
            FoxTheme.settings.moneyFormat
          );
          document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
            el.textContent = formattedTotal;
          });
        }
      } catch (e) {
        self.warn('formatMoney failed:', e);
      }
      try {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: freshCart,
          target,
          line,
          name,
        });
      } catch (e) {
        self.warn('cartUpdate publish failed:', e);
      }
    };

    // Fallback: fetch accurate cart state via /cart.js (confirmed to complete through swym).
    const fetchAndSync = () => {
      nativeFetch(`${FoxTheme.routes.cart_url}.js`)
        .then((r) => r.json())
        .then(syncFrom)
        .catch((e) => {
          self.error('[CartItems] fetchAndSync failed:', e);
          self._pendingUpdates.delete(line);
        });
    };

    // Watchdog: fires only if XHR never called back within 600ms (swym interference or network failure).
    const watchdog = setTimeout(() => {
      if (self._pendingUpdates.has(line)) fetchAndSync();
    }, 600);

    this._postCartChange(changeBody, {
      onLoad: (xhr) => {
        clearTimeout(watchdog);
        if (xhr.status < 400) {
          try {
            syncFrom(JSON.parse(xhr.responseText));
          } catch (_e) {
            fetchAndSync();
          }
        } else {
          // 422 / error: fetch accurate state (section re-render may need to restore items).
          fetchAndSync();
        }
      },
      onNetworkError: () => {
        clearTimeout(watchdog);
        fetchAndSync();
      },
    });
  }

  displayQuantityError(errorMessage) {
    const errorEl = document.createElement('div');
    errorEl.className = 'cart-error-message';
    errorEl.setAttribute('role', 'alert');
    this.insertAdjacentElement('beforeend', errorEl);
    // Set textContent after insertion: live region must be in DOM before mutation fires
    errorEl.textContent = errorMessage;
    setTimeout(() => errorEl.remove(), 5000);
  }

  onCartError(errors, target, line) {
    if (line !== undefined) {
      const sectionId = FoxTheme.utils.getSectionId(this);
      document
        .querySelectorAll(`#Loader-${sectionId}-${line}`)
        .forEach((el) => el.classList.remove('btn--loading'));
    } else {
      this.querySelectorAll('.btn--loading').forEach((el) => el.classList.remove('btn--loading'));
    }
    this.error('Cart error:', errors);
    const message =
      typeof errors === 'string' ? errors : 'Unable to update cart. Please try again.';
    this.displayQuantityError(message);
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }

  _hideLoaderForLine(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => loader.classList.remove('btn--loading'));
    }
  }
}

customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.boundHandleClick = (event) => {
      event.preventDefault();
      // Stop the event reaching CartItems' delegation handler so updateQuantity
      // is only called once (this element was properly upgraded as a customized
      // built-in, so it wins; delegation is the fallback for when it isn't).
      event.stopPropagation();

      const cartItems = this.closest('cart-items');
      if (!cartItems) return;
      cartItems.updateQuantity(this.dataset.index, 0, undefined, undefined, this.dataset.key);
    };

    this.addEventListener('click', this.boundHandleClick);
  }

  disconnectedCallback() {
    if (this.boundHandleClick) {
      this.removeEventListener('click', this.boundHandleClick);
      this.boundHandleClick = null;
    }
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });
class CartNote extends HTMLElement {
  constructor() {
    super();

    this.boundOnChange = FoxTheme.utils.debounce(this.onChange.bind(this), 300);
    this.addEventListener('change', this.boundOnChange);

    if (this.button && this.cartNoteDetailsSummary) {
      this.boundHandleButtonClick = () => {
        this.cartNoteDetailsSummary.close();
      };
      this.button.addEventListener('click', this.boundHandleButtonClick);
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const textarea = event.target;
    const body = JSON.stringify({ note: textarea.value });
    (window._nativeFetch || fetch)(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body },
    }).catch((e) => {
      // Briefly indicate save failure so the user knows to retry
      if (window.CartDebug) console.warn('[CartNote] Failed to save note');
      const isDev =
        window.location.hostname === 'localhost' ||
        new URLSearchParams(window.location.search).has('preview_theme_id');
      if (isDev) console.error('[CartNote] onChange failed:', e);
      textarea.style.outline = '2px solid red';
      setTimeout(() => {
        if (textarea.isConnected) textarea.style.outline = '';
      }, 3000);
    });
  }

  disconnectedCallback() {
    // CRITICAL FIX: Remove all event listeners when element is disconnected
    if (this.boundOnChange) {
      this.removeEventListener('change', this.boundOnChange);
      this.boundOnChange = null;
    }
    if (this.button && this.boundHandleButtonClick) {
      this.button.removeEventListener('click', this.boundHandleButtonClick);
      this.boundHandleButtonClick = null;
    }
  }
}
customElements.define('cart-note', CartNote);

class CalculateShipping extends ModalComponent {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (
      name === 'data-show' &&
      newValue === 'true' &&
      !this.isCountrySetup &&
      this.countryProvince
    ) {
      const template = this.countryProvince.querySelector('template');
      if (!template) return;
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    if (!this.countryElement) return;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex(
          (option) => option.textContent === this.dataset.country
        )
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex];

    // CRITICAL FIX: Add error handling for JSON.parse
    // If data attribute is corrupted, this would crash the page
    let provinces = [];
    try {
      if (option.dataset.provinces) {
        provinces = JSON.parse(option.dataset.provinces);
      }
    } catch (err) {
      this.error('Failed to parse provinces data:', err);
      // Fallback: treat as no provinces available
      provinces = [];
    }

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    // Clear select options safely
    this.provinceElement.textContent = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    // Conditional logging setup
    const isDev =
      window.location.hostname === 'localhost' ||
      new URLSearchParams(window.location.search).has('preview_theme_id');
    // eslint-disable-next-line no-console
    this.log = isDev ? console.log.bind(console) : () => {};
    this.warn = isDev ? console.warn.bind(console) : () => {};
    this.error = isDev ? console.error.bind(console) : () => {};

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    if (!this.submitButton) return;
    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    // CRITICAL FIX: Add null checks for form elements
    // If HTML is missing expected fields, querySelector returns null
    const zipElement = this.querySelector('[name="address[zip]"]');
    const countryElement = this.querySelector('[name="address[country]"]');
    const provinceElement = this.querySelector('[name="address[province]"]');

    if (!zipElement || !countryElement || !provinceElement) {
      this.error('Shipping form missing required fields');
      this.formatError({ form: 'Required shipping fields are missing' });
      return;
    }

    const zip = zipElement.value,
      country = countryElement.value,
      province = provinceElement.value;

    if (!zip || !country) {
      this.formatError({ form: 'Please fill in all required fields' });
      return;
    }

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    (window._nativeFetch || fetch)(sectionUrl, {
      ...FoxTheme.utils.fetchConfig('javascript'),
      ...{ body },
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        this.error(e);
        this.formatError({ message: 'Unable to calculate shipping. Please try again.' });
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    // Build DOM safely to prevent XSS
    const container = document.createElement('div');
    container.className = 'alert alert--error blocks-radius grid gap-2';

    const title = document.createElement('p');
    title.className = 'font-body-bolder m-0';
    title.textContent = FoxTheme.shippingCalculatorStrings.error;

    const list = document.createElement('ul');
    list.className = 'list-disc grid gap-1 text-sm';
    list.setAttribute('role', 'list');

    Object.keys(errors).forEach((errorKey) => {
      const li = document.createElement('li');
      li.textContent = errors[errorKey];
      list.appendChild(li);
    });

    container.appendChild(title);
    container.appendChild(list);
    this.resultsElement.replaceChildren(container);
  }

  formatShippingRates(shippingRates) {
    // Build DOM safely to prevent XSS
    const container = document.createElement('div');
    container.className = `alert blocks-radius alert--${
      shippingRates.length === 0 ? 'error' : 'success'
    } grid gap-2 leading-tight`;

    const title = document.createElement('p');
    title.className = 'font-body-bolder m-0';
    title.textContent =
      shippingRates.length === 0
        ? FoxTheme.shippingCalculatorStrings.notFound
        : shippingRates.length === 1
          ? FoxTheme.shippingCalculatorStrings.oneResult
          : FoxTheme.shippingCalculatorStrings.multipleResults;

    container.appendChild(title);

    if (shippingRates.length > 0) {
      const list = document.createElement('ul');
      list.className = 'list-disc grid gap-1 text-sm';
      list.setAttribute('role', 'list');

      shippingRates.forEach(({ presentment_name, currency, price }) => {
        const li = document.createElement('li');
        li.textContent = `${presentment_name}: ${currency} ${price}`;
        list.appendChild(li);
      });

      container.appendChild(list);
    }

    this.resultsElement.replaceChildren(container);
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();

    // Conditional logging setup
    const isDev =
      window.location.hostname === 'localhost' ||
      new URLSearchParams(window.location.search).has('preview_theme_id');
    // eslint-disable-next-line no-console
    this.log = isDev ? console.log.bind(console) : () => {};
    this.warn = isDev ? console.warn.bind(console) : () => {};
    this.error = isDev ? console.error.bind(console) : () => {};

    // CRITICAL FIX: Track resources for cleanup
    this.carousel = null;
    this.initFetchId = null;
    this.boundInit = this.init.bind(this);

    if ('requestIdleCallback' in window) {
      this.initFetchId = requestIdleCallback(this.boundInit, { timeout: 1500 });
    } else {
      FoxTheme.Motion.inView(this, this.boundInit, { margin: '600px 0px 600px 0px' });
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  init() {
    (window._nativeFetch || fetch)(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector(
          'cart-drawer-products-recommendation'
        );
        if (recommendations && recommendations.textContent.trim().length) {
          const productCount = recommendations.querySelectorAll('.product-card');
          // Use replaceChildren for safer DOM manipulation
          this.replaceChildren(
            ...Array.from(recommendations.children).map((child) => child.cloneNode(true))
          );
          this.initCarousel();
          if (productCount.length > 0) this.classList.remove('hidden');
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section').remove();
          this.classList.add('hidden');
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch((e) => {
        this.error(e);
      });
  }

  initCarousel() {
    this.carousel = new FoxTheme.Carousel(this.slideContainer, {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
    });

    this.carousel && this.carousel.init();
  }

  disconnectedCallback() {
    // CRITICAL FIX: Clean up carousel and cancel pending initialization
    if (this.carousel && typeof this.carousel.destroy === 'function') {
      this.carousel.destroy();
      this.carousel = null;
    }

    // Cancel pending requestIdleCallback
    if (this.initFetchId && 'cancelIdleCallback' in window) {
      cancelIdleCallback(this.initFetchId);
      this.initFetchId = null;
    }

    // Clean up bound function
    this.boundInit = null;
  }
}

customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };

    // Safely initialize goal value
    const minimumAmount = this.dataset.minimumAmount ? Number(this.dataset.minimumAmount) : 0;
    const currencyRate = Number(window.Shopify?.currency?.rate) || 1;

    if (!minimumAmount || isNaN(minimumAmount)) {
      this.goal = 0;
    } else {
      this.goal = minimumAmount * currencyRate;
    }

    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    this.boundCartUpdated = (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    };
    document.addEventListener('cart:updated', this.boundCartUpdated);
  }
  // Fixed: Add disconnectedCallback to remove event listener
  disconnectedCallback() {
    if (this.boundCartUpdated) {
      document.removeEventListener('cart:updated', this.boundCartUpdated);
      this.boundCartUpdated = null;
    }
  }

  updateShippingGloal(amount) {
    // Validate goal before proceeding
    if (!this.goal || isNaN(this.goal) || this.goal <= 0) {
      this.classList.add('hidden');
      return;
    }

    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
      return;
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      const spend = (this.goal - this.cartTotal) * 100;

      // Validate spend is a number before formatting
      if (!isNaN(spend)) {
        const leftToSpendEl = this.querySelector(this.selectors.leftToSpend);
        if (leftToSpendEl) {
          leftToSpendEl.textContent = FoxTheme.Currency.formatMoney(spend, this.money_format);
        }
      }

      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

// Pre-checkout guard: if the GWP component is mid-operation (adding or removing a gift),
// hold checkout until it finishes (up to 3 seconds).
//
// This prevents a race condition where a customer clicks "Checkout" while a gift is
// being added or removed: the cart state on Shopify's end would then not match what
// was computed locally, potentially blocking checkout with "item unavailable" errors.
//
// Handles two entry points:
//   - cart-drawer checkout link (.cart-drawer__checkout-link) — intercepts click
//   - main cart form (button with form="cart" and name="checkout") — intercepts submit
(function () {
  function gwpIsUpdating() {
    const gwp = document.querySelector('cora-gift-with-purchase');
    return gwp && gwp._gwpUpdating;
  }

  // Cart-drawer checkout link (direct /checkout navigation)
  document.addEventListener('click', function (event) {
    const link = event.target.closest('.cart-drawer__checkout-link');
    if (!link) return;
    if (!gwpIsUpdating()) return;

    event.preventDefault();
    link.classList.add('is-loading');
    link.setAttribute('aria-disabled', 'true');
    const href = link.href;
    const deadline = Date.now() + 3000;
    const poll = setInterval(function () {
      if (!gwpIsUpdating() || Date.now() >= deadline) {
        clearInterval(poll);
        window.location.href = href;
      }
    }, 50);
  });

  // Main cart form (submit-based checkout)
  function isCheckoutForm(form) {
    if (form.querySelector('[name="checkout"]')) return true;
    if (form.id && document.querySelector('[name="checkout"][form="' + form.id + '"]')) return true;
    return false;
  }

  document.addEventListener('submit', function (event) {
    if (!isCheckoutForm(event.target)) return;
    if (!gwpIsUpdating()) return;

    // GWP is busy — hold the submission until it finishes or 3s elapses
    event.preventDefault();
    const form = event.target;
    const deadline = Date.now() + 3000;
    const poll = setInterval(function () {
      if (!gwpIsUpdating() || Date.now() >= deadline) {
        clearInterval(poll);
        form.submit();
      }
    }, 50);
  });
})();
